import { useEffect, useMemo, useRef } from "react"
import { AuthCodeRef } from "react-auth-code-input"
import { useForm } from "react-hook-form"

import { PROFILE_DELETE_CODE_LENGTH } from "shared/constants/app-constants"
import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import { useEvent } from "shared/lib/useEvent"

import {
  DataTableOverlayViewProps,
  ProfileDeleteCodeFormData
} from "../ui/ProfileDeleteCodeOverlayView"

export interface UseProfileDeleteCodeOverlayViewModelProps {
  remainingAttempts: number | null
  isVerifying: boolean
  isCodeCorrect: boolean
  verifyCode: (formData: ProfileDeleteCodeFormData) => Promise<boolean>
  onSubmit: (formData: ProfileDeleteCodeFormData) => Promise<boolean>
}

export function useProfileDeleteCodeOverlayViewModel({
  remainingAttempts,
  isVerifying,
  isCodeCorrect,
  verifyCode,
  onSubmit
}: UseProfileDeleteCodeOverlayViewModelProps): DataTableOverlayViewProps {
  const { closeOverlay } = useCurrentOverlay()
  const codeInputRef = useRef<AuthCodeRef>(null)
  const form = useForm({
    defaultValues: { code: "" }
  })

  const handleVerify = useEvent((formData: ProfileDeleteCodeFormData) => {
    verifyCode(formData)
      .then(success => {
        if (!success) {
          codeInputRef.current?.clear()
        }
      })
      .catch(() => codeInputRef.current?.clear())
  })

  const memoizedSubmit = useMemo(
    () => form.handleSubmit(onSubmit),
    [form.handleSubmit, onSubmit]
  )

  useEffect(() => {
    const subscription = form.watch(value => {
      if (value.code && value?.code?.length === PROFILE_DELETE_CODE_LENGTH) {
        handleVerify({ code: value.code })
      }
    })
    return () => subscription.unsubscribe()
  }, [form.watch])

  return {
    codeInputRef,
    form,
    remainingAttempts,
    isVerifying,
    isCodeCorrect,
    isSubmitDisabled: isVerifying || !isCodeCorrect,
    isInputDisabled: remainingAttempts === 0 || isCodeCorrect || isVerifying,
    onClose: closeOverlay,
    onSubmit: memoizedSubmit
  }
}
